import type { RuleGraphDto } from "@intelligems/ig-types";
import type { RuleGraphNode } from "@intelligems/ig-types";
import { cartInputPosition } from "../layout";
import { layoutRuleGraph } from "../layout";
import { RULE_GRAPH_NODE_HEIGHT } from "../layout";
import { RULE_GRAPH_RESULT_WIDTH } from "../layout";
import type { RuleGraphLayout } from "../layout";
import { chipConditionOf } from "../conditionSubject";
import { conditionSubjectOf } from "../conditionSubject";
import { inputHeaderSubject } from "../conditionSubject";
import type { RuleGraphFlowNode } from "../ruleGraphNodeData/RuleGraphFlowNode";
import type { RuleGraphFlowNodeData } from "../ruleGraphNodeData/RuleGraphFlowNodeData";
import { ruleGraphFlowNodeSize } from "../ruleGraphFlowNodeSize";
import type { RuleGraphOverlaysByNodeId } from "../types";
import { RULE_GRAPH_CART_INPUT_NODE_ID } from "../types";
import { hiddenRuleGraphNodeIds } from "./hiddenRuleGraphNodeIds";

export const getRuleGraphFlowNodes = ({
  graph,
  selectedNodeId,
  onSelectNode,
  overlaysByNodeId,
  skeleton = false,
  layout: suppliedLayout,
  onMeasure,
}: {
  graph: RuleGraphDto;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  layout?: RuleGraphLayout;
  onMeasure?: RuleGraphFlowNodeData["onMeasure"];
  overlaysByNodeId?: RuleGraphOverlaysByNodeId;
  /** Building state: every card renders shimmer bars, nothing is clickable. */
  skeleton?: boolean;
}): RuleGraphFlowNode[] => {
  const layout = suppliedLayout ?? layoutRuleGraph(graph);
  const { positionsByNodeId, priorityByBranchId, mode, leadInNodeIds } = layout;
  const showInputHeader = mode === "branchless" || leadInNodeIds.length > 0;
  const hasScenarioOverlay = !skeleton && overlaysByNodeId !== undefined;
  const hidden =
    mode === "base"
      ? new Set<string>()
      : hiddenRuleGraphNodeIds(
          graph,
          skeleton ? undefined : overlaysByNodeId,
          showInputHeader
        );
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const inputSubject = inputHeaderSubject(graph);
  // The chip a branch header shows. A fallback row runs only after the row
  // above ran and came up empty, so a condition that repeats that row's is
  // already satisfied — printing it again composes "If nothing matches and
  // cart contains Snowboard" under a row that already said so. A condition
  // that differs is the row's own narrowing and stays — and so does one that
  // merely reads the same about a different context ("Standard" the delivery
  // option is not "Standard" the cart product).
  const headerConditionOf = (node: RuleGraphNode): string | undefined => {
    const own = chipConditionOf(node);
    if (own === undefined || (priorityByBranchId.get(node.id) ?? 1) <= 1) {
      return own;
    }
    const parentEdge = graph.edges.find(
      (edge) => edge.kind === "fallback" && edge.to === node.id
    );
    const source = nodesById.get(parentEdge?.from ?? "");
    const parent = source?.branchId ? nodesById.get(source.branchId) : source;
    return parent !== undefined &&
      chipConditionOf(parent) === own &&
      conditionSubjectOf(parent) === conditionSubjectOf(node)
      ? undefined
      : own;
  };

  // The run's cart, at the head of the rows — only while a run is on the
  // canvas (its overlay carries the cart), never while the graph is being
  // rebuilt, and only where there are honest rows to head.
  const cartOverlay = skeleton
    ? undefined
    : overlaysByNodeId?.[RULE_GRAPH_CART_INPUT_NODE_ID];
  const cartPosition =
    cartOverlay?.cartInput === undefined
      ? null
      : cartInputPosition(layout, graph);
  const cartInputNodes: RuleGraphFlowNode[] =
    cartOverlay?.cartInput !== undefined && cartPosition !== null
      ? [
          {
            id: RULE_GRAPH_CART_INPUT_NODE_ID,
            type: "ruleGraphCartInput",
            position: cartPosition,
            width: RULE_GRAPH_RESULT_WIDTH,
            height: RULE_GRAPH_NODE_HEIGHT,
            style: { pointerEvents: "all" },
            data: {
              cartInput: cartOverlay.cartInput,
              onMeasure,
              isActive: cartOverlay.active === true,
            },
            selected: false,
            draggable: false,
          },
        ]
      : [];

  const graphNodes: RuleGraphFlowNode[] = graph.nodes
    .filter((node) => !hidden.has(node.id))
    .map((node) => {
      const overlay = skeleton ? undefined : overlaysByNodeId?.[node.id];
      const hasResult = overlay?.result !== undefined;
      const isLaneHeader =
        mode !== "base" &&
        (node.kind === "branch" ||
          node.kind === "fallback-terminal" ||
          (showInputHeader && node.kind === "input"));
      const size = ruleGraphFlowNodeSize({
        kind: node.kind,
        isLaneHeader,
        hasResult,
      });

      return {
        id: node.id,
        type: "ruleGraphNode",
        position: positionsByNodeId.get(node.id) ?? { x: 0, y: 0 },
        ...size,
        style: { pointerEvents: "all" },
        data: {
          node,
          isLaneHeader,
          priority: priorityByBranchId.get(node.id),
          headerCondition:
            node.kind === "branch" ? headerConditionOf(node) : undefined,
          headerSubject:
            node.kind === "branch"
              ? conditionSubjectOf(node)
              : node.kind === "input"
                ? inputSubject
                : "cart",
          isSelected: node.id === selectedNodeId,
          onSelect: onSelectNode,
          overlay,
          hasScenarioOverlay,
          skeleton,
          onMeasure: overlay?.result ? onMeasure : undefined,
        },
        selected: node.id === selectedNodeId,
        draggable: false,
      };
    });
  return [...cartInputNodes, ...graphNodes];
};
